import React, { useState, useEffect, useRef } from 'react';
import StatBox from '../ui/StatBox';
import useInterval from '../../hooks/useInterval';
import { playCorrectSound, playWrongSound, playMissSound, playSuccessSound } from '../../utils/audio';
import { api } from '../../utils/api';
import { User } from '../../types';
import { GameIntro, GameOver } from './GameShell';
import { ScorePops, useScorePops } from '../ui/ScorePops';

const BALLOONS = [
    'bg-gradient-to-br from-pink-400 to-rose-500',
    'bg-gradient-to-br from-sky-400 to-indigo-500',
    'bg-gradient-to-br from-emerald-400 to-teal-500',
    'bg-gradient-to-br from-violet-400 to-fuchsia-500',
    'bg-gradient-to-br from-orange-400 to-red-400',
];

type WordOnScreen = {
    id: number;
    text: string;
    x: number;
    y: number;
    createdAt: number;
    isChallenge?: boolean;
};

interface WordBurstGameProps {
  user?: User;
}

const WordBurstGame: React.FC<WordBurstGameProps> = ({ user }) => {
    const WORD_LIFESPAN = 5000;
    const XP_PER_LEVEL = 50;
    
    const [allWords, setAllWords] = useState<string[]>([]);
    const [wordsOnScreen, setWordsOnScreen] = useState<WordOnScreen[]>([]);
    const [typedText, setTypedText] = useState("");
    const [score, setScore] = useState(0);
    const [level, setLevel] = useState(1);
    const [xp, setXp] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [gameState, setGameState] = useState<'ready' | 'playing' | 'over'>('ready');
    const inputRef = useRef<HTMLInputElement>(null);
    const gameAreaRef = useRef<HTMLDivElement>(null);
    const { pops, pop } = useScorePops();

    useEffect(() => {
        const fetchWords = async () => {
            const data = await api.get<string[]>('/api/v1/game/words');
            setAllWords(data);
        };
        fetchWords();
    }, []);

    const startGame = () => {
        setGameState('playing');
        setScore(0);
        setLevel(1);
        setXp(0);
        setTimeLeft(60);
        setWordsOnScreen([]);
        setTypedText("");
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    useEffect(() => {
        if (gameState === 'over' && user && user.user_id !== 'guest') {
            api.post('/api/v1/student/game-progress', { game_name: 'word-burst', high_score: score, increment_attempts: true }).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (gameState !== 'playing') return;

            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setTypedText(currentTypedText => {
                    const wordToBurst = currentTypedText.trim();
                    if (!wordToBurst) return '';

                    setWordsOnScreen(currentWords => {
                        const foundWordIndex = currentWords.findIndex(w => w.text === wordToBurst);
                        if (foundWordIndex > -1) {
                            const foundWord = currentWords[foundWordIndex];
                            playCorrectSound();
                            const area = gameAreaRef.current;
                            if (area) pop(`+${foundWord.text.length}${foundWord.isChallenge ? ' ⭐' : ''}`, ((foundWord.x + foundWord.text.length * 7) / area.offsetWidth) * 100, (foundWord.y / area.offsetHeight) * 100, foundWord.isChallenge ? '#d97706' : '#7c3aed');
                            setScore(s => s + foundWord.text.length);
                            setXp(currentXp => {
                                const xpGained = foundWord.isChallenge ? foundWord.text.length * 2 : foundWord.text.length;
                                const newXp = currentXp + xpGained;
                                if (newXp >= XP_PER_LEVEL) {
                                    setLevel(l => l + 1);
                                    playSuccessSound();
                                    return newXp % XP_PER_LEVEL;
                                }
                                return newXp;
                            });
                            return currentWords.filter(w => w.id !== foundWord.id);
                        } else {
                            playWrongSound();
                            gameAreaRef.current?.classList.add('shake');
                            setTimeout(() => gameAreaRef.current?.classList.remove('shake'), 500);
                            return currentWords;
                        }
                    });
                    return '';
                });
            } else if (e.key === 'Backspace') {
                e.preventDefault();
                setTypedText(t => t.slice(0, -1));
            } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                setTypedText(t => t + e.key);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameState, XP_PER_LEVEL, pop]);

    useInterval(() => {
        if (gameState !== 'playing') return;
        if (timeLeft > 0) setTimeLeft(t => t - 1);
        else setGameState('over');
    }, 1000);

    useInterval(() => {
        if (gameState !== 'playing' || allWords.length === 0 || !gameAreaRef.current) return;
        
        const gameArea = gameAreaRef.current;
        
        const maxWordLength = 3 + level;
        const availableWords = allWords.filter(w => w.length <= maxWordLength && w.length > 2);
        if (availableWords.length === 0) return;

        const shouldSpawnChallenge = Math.random() < 0.15;
        let newWordText: string;
        let isChallenge = false;

        if (shouldSpawnChallenge) {
            const longestWords = availableWords.filter(w => w.length === Math.max(...availableWords.map(aw => aw.length)));
            newWordText = longestWords.length > 0 ? longestWords[Math.floor(Math.random() * longestWords.length)] : availableWords[Math.floor(Math.random() * availableWords.length)];
            isChallenge = longestWords.length > 0;
        } else {
            newWordText = availableWords[Math.floor(Math.random() * availableWords.length)];
        }
        
        const newWord: WordOnScreen = {
            id: Date.now(),
            text: newWordText,
            x: Math.random() * (gameArea.offsetWidth - (newWordText.length * 14)),
            y: Math.random() * (gameArea.offsetHeight - 40),
            createdAt: Date.now(),
            isChallenge
        };
        setWordsOnScreen(prev => [...prev, newWord]);
    }, Math.max(400, 2000 - level * 150));

    useInterval(() => {
        if (gameState !== 'playing') return;
        setWordsOnScreen(prevWords => 
            prevWords.filter(w => {
                const isExpired = Date.now() - w.createdAt > WORD_LIFESPAN;
                if (isExpired) playMissSound();
                return !isExpired;
            })
        );
    }, 100);

    if (gameState === 'ready') return (
        <GameIntro icon="💥" title="Word Burst" skill="vocabulary & whole-word typing -- no looking at letters one at a time." accent="text-purple-600" button="bg-gradient-to-r from-violet-500 to-fuchsia-500" onStart={startGame}>
            Word balloons appear on screen. Type a word fully, then press Space or Enter to burst it before it fades. Gold words are worth double XP. Level up to earn tougher words!
        </GameIntro>
    );
    if (gameState === 'over') return (
        <GameOver
            title="Time's Up!"
            message={level >= 4 ? 'What a word wizard!' : 'Nice bursting -- go for a higher level!'}
            stars={level >= 5 ? 3 : level >= 3 ? 2 : score > 0 ? 1 : 0}
            stats={[{ label: 'Final Score', value: score }, { label: 'Level Reached', value: level }]}
            button="bg-gradient-to-r from-violet-500 to-fuchsia-500"
            onRestart={startGame}
        />
    );

    return (
        <div className="w-full" onClick={() => inputRef.current?.focus()}>
            <div className="flex flex-wrap justify-around items-center mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
                <StatBox label="Time Left" value={timeLeft} />
                <StatBox label="Score" value={score} />
                <div className="flex flex-col items-center px-4 py-2 min-w-[120px] rounded-2xl bg-white shadow-md ring-2 ring-white/70">
                    <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-500">Level {level}</span>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mt-1"><div className="bg-gradient-to-r from-emerald-400 to-lime-400 h-2.5 rounded-full transition-all" style={{ width: `${(xp / XP_PER_LEVEL) * 100}%` }}></div></div>
                </div>
            </div>
            <div ref={gameAreaRef} className="relative h-96 bg-gradient-to-br from-indigo-200 via-pink-100 to-amber-100 rounded-2xl overflow-hidden border-4 border-white shadow-inner">
                <span className="fun-float absolute top-6 left-[6%] text-4xl opacity-40" aria-hidden="true">🎈</span>
                <span className="fun-float absolute bottom-8 right-[8%] text-5xl opacity-40" style={{ animationDelay: '2.5s' }} aria-hidden="true">🎉</span>
                <span className="fun-float absolute top-1/2 left-1/2 text-3xl opacity-30" style={{ animationDelay: '1.2s' }} aria-hidden="true">✨</span>
                {wordsOnScreen.map(word => (
                    <div key={word.id} className="absolute fun-pop" style={{ left: word.x, top: word.y }}>
                       <div className={`relative px-3 py-1.5 overflow-hidden ${word.isChallenge ? 'bg-gradient-to-br from-yellow-300 to-amber-400 text-gray-900 ring-4 ring-yellow-200 animate-pulse' : `${BALLOONS[word.id % BALLOONS.length]} text-white`} rounded-full border-4 border-white shadow-lg font-mono text-xl font-bold`}>
                           {word.isChallenge && <span aria-hidden="true">⭐ </span>}{word.text}
                           <div className="absolute bottom-0 left-0 h-1.5 bg-white/80" style={{ width: `${Math.max(0, 100 - ((Date.now() - word.createdAt) / WORD_LIFESPAN) * 100)}%`, transition: 'width 0.1s linear' }}></div>
                       </div>
                    </div>
                ))}
                <ScorePops pops={pops} />
            </div>
            <div className="mt-4 p-3 bg-white rounded-full border-4 border-violet-200 shadow-md text-center h-16 flex items-center justify-center">
                <span className="font-mono text-2xl font-bold text-violet-700">{typedText}</span>
                <span className="font-mono text-2xl text-fuchsia-500 cursor-blink">|</span>
            </div>
            <input ref={inputRef} type="text" className="opacity-0 absolute w-0 h-0" autoFocus onBlur={({ target }) => target.focus()} />
        </div>
    );
};

export default WordBurstGame;